import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, Info } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';

const CoverageStartDate = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const orgType = formData.org_type;
  
  // Determine coverage type based on org_type
  const isEventBased = orgType === 'Tournament or Event Operator';
  const isCampBased = orgType === 'Sports Camp / Clinic';
  const isAnnual = !isEventBased && !isCampBased;

  const { register, handleSubmit, watch, formState: { errors }, setError, clearErrors } = useForm({
    defaultValues: {
      // Event fields
      event_start_date: formData.event_start_date || '',
      event_end_date: formData.event_end_date || '',
      // Camp fields
      camp_start_date: formData.camp_start_date || '',
      camp_end_date: formData.camp_end_date || '',
      // Annual policy fields
      policy_effective_date: formData.policy_effective_date || formData.coverage_start_date || '',
    }
  });

  const eventStartDate = watch('event_start_date');
  const eventEndDate = watch('event_end_date');
  const campStartDate = watch('camp_start_date');
  const campEndDate = watch('camp_end_date');

  // Validate end date is not before start date
  useEffect(() => {
    if (isEventBased && eventStartDate && eventEndDate) {
      if (new Date(eventEndDate) < new Date(eventStartDate)) {
        setError('event_end_date', { message: 'End date cannot be before start date' });
      } else {
        clearErrors('event_end_date');
      }
    }
    if (isCampBased && campStartDate && campEndDate) {
      if (new Date(campEndDate) < new Date(campStartDate)) {
        setError('camp_end_date', { message: 'End date cannot be before start date' });
      } else {
        clearErrors('camp_end_date');
      }
    }
  }, [eventStartDate, eventEndDate, campStartDate, campEndDate, isEventBased, isCampBased, setError, clearErrors]);

  const onSubmit = (data) => {
    let coverageData = {};

    if (isEventBased) {
      // Validate dates
      if (new Date(data.event_end_date) < new Date(data.event_start_date)) {
        setError('event_end_date', { message: 'End date cannot be before start date' });
        return;
      }
      coverageData = {
        coverage_type: 'event',
        event_start_date: data.event_start_date,
        event_end_date: data.event_end_date,
        coverage_start_date: data.event_start_date, // For backwards compatibility
      };
    } else if (isCampBased) {
      // Validate dates
      if (new Date(data.camp_end_date) < new Date(data.camp_start_date)) {
        setError('camp_end_date', { message: 'End date cannot be before start date' });
        return;
      }
      coverageData = {
        coverage_type: 'camp',
        camp_start_date: data.camp_start_date,
        camp_end_date: data.camp_end_date,
        coverage_start_date: data.camp_start_date, // For backwards compatibility
      };
    } else {
      // Calculate end date (12 months from start)
      const startDate = new Date(data.policy_effective_date);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 12);
      
      coverageData = {
        coverage_type: 'annual',
        policy_effective_date: data.policy_effective_date,
        policy_term_months: 12,
        coverage_start_date: data.policy_effective_date, // For backwards compatibility
      };
    }

    updateFormData(coverageData);
    nextStep();
  };

  const today = new Date().toISOString().split('T')[0];

  // Calculate policy end date for display
  const calculateEndDate = (startDate) => {
    if (!startDate) return '';
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + 12);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Event-Based Coverage */}
      {isEventBased && (
        <>
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Event Coverage:</strong> Coverage will apply only during the selected event dates.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div>
              <Label htmlFor="event_start_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Event Start Date *
              </Label>
              <Input
                id="event_start_date"
                data-testid="event-start-date-input"
                type="date"
                min={today}
                {...register('event_start_date', { required: 'Event start date is required' })}
                className="h-12 mt-1.5"
              />
              {errors.event_start_date && (
                <p className="text-sm text-red-600 mt-1">{errors.event_start_date.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="event_end_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Event End Date *
              </Label>
              <Input
                id="event_end_date"
                data-testid="event-end-date-input"
                type="date"
                min={eventStartDate || today}
                {...register('event_end_date', { required: 'Event end date is required' })}
                className="h-12 mt-1.5"
              />
              {errors.event_end_date && (
                <p className="text-sm text-red-600 mt-1">{errors.event_end_date.message}</p>
              )}
            </div>
          </div>

          {eventStartDate && eventEndDate && !errors.event_end_date && (
            <div className="bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-800">
                Coverage Period: {new Date(eventStartDate).toLocaleDateString()} - {new Date(eventEndDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </>
      )}

      {/* Camp/Clinic-Based Coverage */}
      {isCampBased && (
        <>
          <Alert className="bg-green-50 border-green-200">
            <Info className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>Camp/Clinic Coverage:</strong> Coverage will apply only during the selected camp or clinic dates.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div>
              <Label htmlFor="camp_start_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Camp / Clinic Start Date *
              </Label>
              <Input
                id="camp_start_date"
                data-testid="camp-start-date-input"
                type="date"
                min={today}
                {...register('camp_start_date', { required: 'Camp start date is required' })}
                className="h-12 mt-1.5"
              />
              {errors.camp_start_date && (
                <p className="text-sm text-red-600 mt-1">{errors.camp_start_date.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="camp_end_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Camp / Clinic End Date *
              </Label>
              <Input
                id="camp_end_date"
                data-testid="camp-end-date-input"
                type="date"
                min={campStartDate || today}
                {...register('camp_end_date', { required: 'Camp end date is required' })}
                className="h-12 mt-1.5"
              />
              {errors.camp_end_date && (
                <p className="text-sm text-red-600 mt-1">{errors.camp_end_date.message}</p>
              )}
            </div>
          </div>

          {campStartDate && campEndDate && !errors.camp_end_date && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-medium text-green-800">
                Coverage Period: {new Date(campStartDate).toLocaleDateString()} - {new Date(campEndDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </>
      )}

      {/* Annual Policy Coverage */}
      {isAnnual && (
        <>
          <Alert className="bg-purple-50 border-purple-200">
            <Info className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-800">
              <strong>Annual Policy:</strong> Coverage will apply for a 12-month policy term from your selected start date.
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="policy_effective_date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-500" />
              Policy Effective Date *
            </Label>
            <p className="text-sm text-slate-600 mt-1 mb-3">
              When would you like your 12-month policy to begin?
            </p>
            <Input
              id="policy_effective_date"
              data-testid="policy-effective-date-input"
              type="date"
              min={today}
              {...register('policy_effective_date', { required: 'Policy effective date is required' })}
              className="h-12"
            />
            {errors.policy_effective_date && (
              <p className="text-sm text-red-600 mt-1">{errors.policy_effective_date.message}</p>
            )}
          </div>

          {watch('policy_effective_date') && (
            <div className="bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600 uppercase tracking-wide font-semibold">Policy Term</p>
                  <p className="text-lg font-bold text-slate-900">12 Months</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-purple-600 uppercase tracking-wide font-semibold">Coverage End Date</p>
                  <p className="text-lg font-bold text-slate-900">{calculateEndDate(watch('policy_effective_date'))}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <ThemedButton 
        type="submit"
        data-testid="coverage-date-next-btn"
        variant="wizard" size="full"
      >
        Next: Coverage Package
      </ThemedButton>
    </form>
  );
};

export default CoverageStartDate;
